import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useOnboarding } from "./OnboardingProvider";

const TOOLTIP_WIDTH = 340;
const GAP = 16;
const SPOT_PADDING = 10;
const ESTIMATED_TOOLTIP_HEIGHT = 240;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const OnboardingTour = () => {
  const { isOpen, steps, stopTour } = useOnboarding();
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState<Rect | null>(null);
  const [tooltip, setTooltip] = useState<Rect | null>(null);

  const step = steps[index];
  const total = steps.length;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  const measure = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const centered = (): Rect => ({
      top: Math.max(GAP, vh / 2 - ESTIMATED_TOOLTIP_HEIGHT / 2),
      left: clamp(vw / 2 - TOOLTIP_WIDTH / 2, GAP, vw - TOOLTIP_WIDTH - GAP),
      width: TOOLTIP_WIDTH,
      height: ESTIMATED_TOOLTIP_HEIGHT,
    });

    // Étape centrée (ex: bienvenue) ou élément cible introuvable.
    if (!step?.target) {
      setSpot(null);
      setTooltip(centered());
      return;
    }

    const element = document.querySelector<HTMLElement>(step.target);
    const rect = element?.getBoundingClientRect();

    // Élément absent ou masqué (ex: élément réservé au desktop).
    if (!element || !rect || rect.width < 2 || rect.height < 2) {
      setSpot(null);
      setTooltip(centered());
      return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "center" });

    const nextSpot: Rect = {
      top: rect.top - SPOT_PADDING,
      left: rect.left - SPOT_PADDING,
      width: rect.width + SPOT_PADDING * 2,
      height: rect.height + SPOT_PADDING * 2,
    };
    setSpot(nextSpot);

    const placement = step.placement ?? "bottom";
    let left = nextSpot.left + nextSpot.width / 2 - TOOLTIP_WIDTH / 2;
    let top = nextSpot.top + nextSpot.height + GAP;

    if (placement === "top") {
      top = nextSpot.top - ESTIMATED_TOOLTIP_HEIGHT - GAP;
    } else if (placement === "left") {
      left = nextSpot.left - TOOLTIP_WIDTH - GAP;
      top = nextSpot.top + nextSpot.height / 2 - ESTIMATED_TOOLTIP_HEIGHT / 2;
    } else if (placement === "right") {
      left = nextSpot.left + nextSpot.width + GAP;
      top = nextSpot.top + nextSpot.height / 2 - ESTIMATED_TOOLTIP_HEIGHT / 2;
    }

    // Recentrage vertical lorsque le tooltip déborde en bas.
    if (top + ESTIMATED_TOOLTIP_HEIGHT > vh - GAP) {
      top = nextSpot.top - ESTIMATED_TOOLTIP_HEIGHT - GAP;
    }

    left = clamp(left, GAP, vw - TOOLTIP_WIDTH - GAP);
    top = clamp(top, GAP, vh - ESTIMATED_TOOLTIP_HEIGHT - GAP);

    setTooltip({
      top,
      left,
      width: TOOLTIP_WIDTH,
      height: ESTIMATED_TOOLTIP_HEIGHT,
    });
  }, [step]);

  // Réinitialise la position au démarrage ou au changement de tour.
  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen, steps]);

  useEffect(() => {
    if (!isOpen) return;

    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [isOpen, index, measure]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") stopTour();
      if (event.key === "ArrowRight") {
        if (isLast) stopTour();
        else setIndex((i) => Math.min(i + 1, total - 1));
      }
      if (event.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, isLast, total, stopTour]);

  if (!isOpen || steps.length === 0) return null;

  const handleNext = () => {
    if (isLast) stopTour();
    else setIndex((i) => i + 1);
  };

  const handleBack = () => setIndex((i) => Math.max(0, i - 1));

  const spotlightStyle = (): CSSProperties => {
    if (!spot) return {};
    return {
      top: spot.top,
      left: spot.left,
      width: spot.width,
      height: spot.height,
    };
  };

  const tooltipStyle = (): CSSProperties => ({
    top: tooltip?.top ?? 0,
    left: tooltip?.left ?? 0,
    width: TOOLTIP_WIDTH,
  });

  return createPortal(
    <div className="fixed inset-0 z-[99999]" role="dialog" aria-modal="true">
      {/* Fond assombri + découpe en 4 rectangles autour de la zone cible. */}
      {spot ? (
        <>
          <div
            className="absolute bg-gray-900/70"
            style={{ top: 0, left: 0, right: 0, height: spot.top }}
          />
          <div
            className="absolute bg-gray-900/70"
            style={{
              top: spot.top + spot.height,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          />
          <div
            className="absolute bg-gray-900/70"
            style={{
              top: spot.top,
              left: 0,
              width: spot.left,
              height: spot.height,
            }}
          />
          <div
            className="absolute bg-gray-900/70"
            style={{
              top: spot.top,
              left: spot.left + spot.width,
              right: 0,
              height: spot.height,
            }}
          />
          {/* Contour lumineux de la zone mise en évidence. */}
          <div
            className="pointer-events-none absolute rounded-xl"
            style={{
              ...spotlightStyle(),
              boxShadow:
                "0 0 0 2px rgba(255,255,255,0.95), 0 0 28px rgba(163,230,53,0.5)",
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-gray-900/70" />
      )}

      {/* Tooltip */}
      <div
        className="absolute flex flex-col rounded-2xl bg-white shadow-2xl"
        style={tooltipStyle()}
      >
        <div className="flex items-start justify-between px-5 pt-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-green-100">
            Step {index + 1} of {total}
          </p>
          <button
            onClick={stopTour}
            aria-label="Close tour"
            className="-mr-1 -mt-1 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pb-5">
          <h2 className="mb-2 text-lg font-bold text-gray-900">{step.title}</h2>
          <p className="text-sm leading-relaxed text-gray-600">
            {step.content}
          </p>
        </div>

        {/* Barre de progression */}
        <div className="px-5">
          <div className="flex items-center gap-1.5">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= index ? "bg-lime-100" : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between rounded-b-2xl border-t border-gray-100 px-5 py-4">
          <button
            onClick={stopTour}
            className="text-sm font-medium text-gray-400 transition-colors hover:text-gray-600"
          >
            Skip
          </button>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={handleBack}
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex items-center gap-1 rounded-lg bg-lime-100 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-100"
            >
              {isLast ? "Get started" : "Next"}
              {!isLast && <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OnboardingTour;
